import { useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  createPassengerSchema,
  updatePassengerSchema,
  type DocumentType,
  type PassengerDto,
} from "@tc/shared";
import { Button, CountrySelect, Field, Icon, Modal, Segmented, Select, TextInput } from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { anchorNewTab, openFileTab } from "@/lib/download";
import { useLanguage } from "@/lib/i18n/language";
import { rosterApi } from "./api";
import { ApiError } from "@/features/trips/api";

interface PassengerFormProps {
  /** The passenger being edited, or null for a new one. */
  passenger: PassengerDto | null;
  /**
   * The club a new passenger is created for. Required for back office (no implied club); omitted
   * for club users, whose own club is implied server-side.
   */
  clubId?: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const DOC_TYPES: DocumentType[] = ["DNI", "NIE", "PASSPORT"];

/** Add or edit a roster passenger (club or back office). Mirrors the prototype's add-passenger modal. */
export function PassengerForm({ passenger, clubId, onClose, onSaved }: PassengerFormProps) {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const mode = passenger ? "edit" : "create";

  const [firstName, setFirstName] = useState(passenger?.firstName ?? "");
  const [lastName, setLastName] = useState(passenger?.lastName ?? "");
  const [isStaff, setIsStaff] = useState(passenger?.isStaff ?? false);
  const [dateOfBirth, setDateOfBirth] = useState(passenger?.dateOfBirth ?? "");
  const [nationality, setNationality] = useState(passenger?.nationality ?? "");
  const [documentType, setDocumentType] = useState<DocumentType | "">(passenger?.documentType ?? "");
  const [documentNumber, setDocumentNumber] = useState(passenger?.documentNumber ?? "");
  const [documentExpiry, setDocumentExpiry] = useState(passenger?.documentExpiry ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      isStaff,
      dateOfBirth: dateOfBirth || null,
      nationality: nationality.trim() || null,
      documentType: documentType || null,
      documentNumber: documentNumber.trim() || null,
      documentExpiry: documentExpiry || null,
    };

    setSaving(true);
    try {
      if (passenger) {
        const parsed = updatePassengerSchema.safeParse(payload);
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message ?? t("roster.saveError"));
          return;
        }
        await rosterApi.update(passenger.id, parsed.data);
      } else {
        const parsed = createPassengerSchema.safeParse(clubId ? { ...payload, clubId } : payload);
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message ?? t("roster.saveError"));
          return;
        }
        await rosterApi.create(parsed.data);
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("roster.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t(mode === "create" ? "roster.addPassenger" : "roster.editPassenger")}
      width={520}
    >
      <form onSubmit={onSubmit}>
        <div className="modal-body">
          {error && (
            <div className="auth-alert" style={{ marginBottom: 16 }} role="alert">
              <Icon name="alert" size={16} className="aa-ic" />
              <div className="aa-body">{error}</div>
            </div>
          )}
          <div className="form-grid">
            <Field label={t("roster.firstName")} required>
              <TextInput value={firstName} onChange={setFirstName} placeholder="Pau" />
            </Field>
            <Field label={t("roster.lastName")} required>
              <TextInput value={lastName} onChange={setLastName} placeholder="Quemada" />
            </Field>
            <Field label={t("roster.role")}>
              <Segmented
                size="sm"
                value={isStaff ? "staff" : "player"}
                onChange={(v) => setIsStaff(v === "staff")}
                options={[
                  { value: "player", label: t("roster.player") },
                  { value: "staff", label: t("roster.staff") },
                ]}
              />
            </Field>
            <Field label={t("roster.dob")}>
              <TextInput type="date" value={dateOfBirth} onChange={setDateOfBirth} />
            </Field>
            <Field label={t("roster.docType")}>
              <Select
                value={documentType}
                onChange={(v) => setDocumentType(v as DocumentType | "")}
                options={[
                  { value: "", label: t("roster.docTypeNone") },
                  ...DOC_TYPES.map((dt) => ({ value: dt, label: dt })),
                ]}
              />
            </Field>
            <Field label={t("roster.docNum")}>
              <TextInput value={documentNumber} onChange={setDocumentNumber} placeholder="46••••••K" />
            </Field>
            <Field label={t("roster.expiry")}>
              <TextInput type="date" value={documentExpiry} onChange={setDocumentExpiry} />
            </Field>
            <Field label={t("roster.nationality")}>
              <CountrySelect
                value={nationality}
                onChange={setNationality}
                locale={lang}
                emptyLabel={t("roster.nationalityNone")}
              />
            </Field>
          </div>

          {passenger && (
            <DocumentImageField passenger={passenger} onChanged={onSaved} setError={setError} />
          )}
        </div>
        <div className="modal-foot">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("actions.cancel")}
          </Button>
          <Button type="submit" disabled={saving}>
            {t("actions.save")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Optional document-image control, shown on an existing passenger only when the upload feature
 * flag is on (a new passenger must be saved first to own a storage namespace). Uploads via the
 * three-step signed-URL dance; the bytes go straight to storage, only the key reaches our API.
 */
function DocumentImageField({
  passenger,
  onChanged,
  setError,
}: {
  passenger: PassengerDto;
  onChanged: () => void | Promise<void>;
  setError: (m: string | null) => void;
}) {
  const { t } = useTranslation();
  const { me } = useAuthz();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [hasImage, setHasImage] = useState(passenger.hasDocumentImage);

  if (!me?.features?.passengerDocUpload) return null;

  async function onPick(file: File) {
    setError(null);
    setBusy(true);
    try {
      const target = await rosterApi.signUpload(passenger.id);
      await rosterApi.uploadBytes(target, file);
      await rosterApi.attachImage(passenger.id, { key: target.key });
      setHasImage(true);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("roster.uploadError"));
    } finally {
      setBusy(false);
    }
  }

  async function onView() {
    const tab = openFileTab(); // synchronously, inside the click — see lib/download.ts
    setError(null);
    try {
      const { url } = await rosterApi.imageUrl(passenger.id);
      if (tab) tab.deliver(url);
      else anchorNewTab(url);
    } catch (err) {
      tab?.fail();
      setError(err instanceof ApiError ? err.message : t("roster.uploadError"));
    }
  }

  async function onRemove() {
    setError(null);
    setBusy(true);
    try {
      await rosterApi.removeImage(passenger.id);
      setHasImage(false);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("roster.uploadError"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      <Field label={t("roster.docImg")} full hint={t("roster.docImgHint")}>
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          {hasImage ? (
            <>
              <Button type="button" variant="soft" size="sm" icon="eye" onClick={onView} disabled={busy}>
                {t("roster.viewDoc")}
              </Button>
              <Button type="button" variant="ghost" size="sm" icon="x" onClick={onRemove} disabled={busy}>
                {t("roster.removeDoc")}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="soft"
              size="sm"
              icon="upload"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
            >
              {busy ? t("roster.uploading") : t("roster.uploadDoc")}
            </Button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onPick(file);
              e.target.value = "";
            }}
          />
        </div>
      </Field>
    </div>
  );
}
